import { useState, useRef, useEffect } from "react";
import { Box, BoxProps, SxProps, Typography, useTheme } from "@mui/material";
import { CustomMarkdown } from "../../../../custom-markdown/custom-markdown";
import { IoIosArrowForward } from "react-icons/io";
import { IoIosArrowBack } from "react-icons/io";
import { Stage, Layer, Line, Image as KonvaImage, Group } from "react-konva";
import Konva from "konva";
import { KonvaEventObject } from "konva/lib/Node";
import useImage from "use-image";

interface ImageDrawingProps extends BoxProps {
  src: string;
  alt?: string;
  text: string | null;
  isTextOpen?: boolean;
  setIsTextOpen?: (isOpen: boolean) => void;
  selectedColor: string | null;
  setSelectedColor: (color: string | null) => void;
  selectedToolId: string | null;
  setSelectedToolId: (toolId: string | null) => void;
  isClear: boolean;
  setIsClear: (isClear: boolean) => void;
  sx?: SxProps;
}

const getDistance = (
  p1: { x: number; y: number },
  p2: { x: number; y: number }
) => {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
};

const getCenter = (
  p1: { x: number; y: number },
  p2: { x: number; y: number }
) => {
  return {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  };
};

const stagePointToGroupLocal = (
  pointer: { x: number; y: number },
  group: Konva.Group
) => {
  const s = group.scaleX();
  return {
    x: (pointer.x - group.x()) / s,
    y: (pointer.y - group.y()) / s,
  };
};

export const ImageDrawing = ({
  src,
  alt = "Full screen image",
  text,
  isTextOpen,
  setIsTextOpen,
  selectedColor,
  setSelectedColor,
  selectedToolId,
  setSelectedToolId,
  isClear,
  setIsClear,
  sx: boxSx,
  ...boxProps
}: ImageDrawingProps) => {
  const [image] = useImage(src);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [viewport, setViewport] = useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  }));

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const stageRef = useRef<Konva.Stage>(null);
  const transformGroupRef = useRef<Konva.Group | null>(null);
  const [lines, setLines] = useState<
    {
      points: number[];
      stroke: string;
      opacity: number;
      strokeWidth: number;
      globalCompositeOperation: GlobalCompositeOperation;
    }[]
  >([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isZooming, setIsZooming] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [outerBoxWidth, setOuterBoxWidth] = useState(0);
  const [outerBoxHeight, setOuterBoxHeight] = useState(0);
  const [stageWidth, setStageWidth] = useState(0);
  const [stageHeight, setStageHeight] = useState(0);
  const [fitRect, setFitRect] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const lastCenter = useRef<{ x: number; y: number } | null>(null);
  const lastDist = useRef<number>(0);
  const lastPanTouch = useRef<{
    clientX: number;
    clientY: number;
    stageX: number;
    stageY: number;
  } | null>(null);
  const lastPanMouse = useRef<{
    clientX: number;
    clientY: number;
    stageX: number;
    stageY: number;
  } | null>(null);

  useEffect(() => {
    if (image) {
      setImageSize({ width: image.width, height: image.height });
    }
  }, [image]);

  useEffect(() => {
    if (isClear) {
      setLines([]);
      setIsClear(false);
    }
  }, [isClear]);

  useEffect(() => {
    const onResize = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const vw = viewport.width;
    const vh = viewport.height;
    const iw = imageSize.width;
    const ih = imageSize.height;
    if (vw <= 0 || vh <= 0 || iw <= 0 || ih <= 0) return;

    const imageAspectRatio = iw / ih;
    const windowAspectRatio = vw / vh;

    setOuterBoxWidth(vw);
    setOuterBoxHeight(vh);
    setStageWidth(vw);
    setStageHeight(vh);

    if (imageAspectRatio > windowAspectRatio) {
      const w = vw;
      const h = vw / imageAspectRatio;
      setFitRect({ x: 0, y: (vh - h) / 2, w, h });
    } else {
      const h = vh;
      const w = vh * imageAspectRatio;
      setFitRect({ x: (vw - w) / 2, y: 0, w, h });
    }
  }, [
    imageSize.width,
    imageSize.height,
    viewport.width,
    viewport.height,
  ]);

  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [src]);

  const handleWheel = (e: KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    const scaleBy = 1.05;
    const stage = e.target.getStage();
    const group = transformGroupRef.current;
    if (!stage || !group) return;

    const oldScale = group.scaleX();

    const pointer = stage.getRelativePointerPosition();
    if (!pointer) return;

    const mousePointTo = {
      x: (pointer.x - group.x()) / oldScale,
      y: (pointer.y - group.y()) / oldScale,
    };

    let newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;

    if (newScale < 1) {
      newScale = 1;
    }

    setScale(newScale);
    group.scale({ x: newScale, y: newScale });

    const newPos =
      newScale === 1
        ? { x: 0, y: 0 }
        : {
            x: pointer.x - mousePointTo.x * newScale,
            y: pointer.y - mousePointTo.y * newScale,
          };
    group.position(newPos);
    setPosition(newPos);
    stage.batchDraw();
  };

  const handleMouseDown = (e?: KonvaEventObject<MouseEvent>) => {
    e?.evt.preventDefault();
    const stage = stageRef.current;
    const group = transformGroupRef.current;
    if (!stage || !group) return;

    if (!selectedToolId && scale > 1 && e?.evt) {
      lastPanMouse.current = {
        clientX: e.evt.clientX,
        clientY: e.evt.clientY,
        stageX: group.x(),
        stageY: group.y(),
      };
      return;
    }

    if (
      !selectedToolId ||
      (!selectedColor && selectedToolId !== "eraser")
    ) {
      return;
    }

    const pointer = stage.getRelativePointerPosition();
    if (!pointer) return;
    const point = stagePointToGroupLocal(pointer, group);
    setIsDrawing(true);

    setLines([
      ...lines,
      {
        points: [point.x, point.y],
        stroke: selectedColor ?? "#000000",
        opacity: selectedToolId === "highlighter" ? 0.5 : 1,
        strokeWidth: selectedToolId === "pen" ? 2 : 10,
        globalCompositeOperation:
          selectedToolId === "eraser" ? "destination-out" : "source-over",
      },
    ]);
  };

  const handleTouchStart = (e: KonvaEventObject<TouchEvent>) => {
    if (e.evt.touches.length === 2) {
      e.evt.preventDefault();
      lastPanTouch.current = null;
      const touch1 = e.evt.touches[0];
      const touch2 = e.evt.touches[1];
      setIsZooming(true);
      setIsDrawing(false);

      const p1 = { x: touch1.clientX, y: touch1.clientY };
      const p2 = { x: touch2.clientX, y: touch2.clientY };

      lastCenter.current = getCenter(p1, p2);
      lastDist.current = getDistance(p1, p2);
      return;
    }

    if (
      e.evt.touches.length === 1 &&
      !selectedToolId &&
      scale > 1
    ) {
      e.evt.preventDefault();
      const touch = e.evt.touches[0];
      const group = transformGroupRef.current;
      if (group) {
        lastPanTouch.current = {
          clientX: touch.clientX,
          clientY: touch.clientY,
          stageX: group.x(),
          stageY: group.y(),
        };
      }
      return;
    }

    lastPanTouch.current = null;
    handleMouseDown();
  };

  const handleMouseMove = (e?: KonvaEventObject<MouseEvent>) => {
    e?.evt.preventDefault();
    const stage = stageRef.current;
    const group = transformGroupRef.current;
    if (!stage || !group) return;

    if (
      lastPanMouse.current &&
      !selectedToolId &&
      scale > 1 &&
      e?.evt
    ) {
      const deltaX = e.evt.clientX - lastPanMouse.current.clientX;
      const deltaY = e.evt.clientY - lastPanMouse.current.clientY;
      const newPos = {
        x: lastPanMouse.current.stageX + deltaX,
        y: lastPanMouse.current.stageY + deltaY,
      };
      lastPanMouse.current = {
        clientX: e.evt.clientX,
        clientY: e.evt.clientY,
        stageX: newPos.x,
        stageY: newPos.y,
      };
      group.position(newPos);
      setPosition(newPos);
      stage.batchDraw();
      return;
    }

    if (!isDrawing) return;
    const pointer = stage.getRelativePointerPosition();
    if (!pointer) return;
    const point = stagePointToGroupLocal(pointer, group);

    const lastLine = lines[lines.length - 1];
    const newLines = lines.slice(0, -1);
    lastLine.points = lastLine.points.concat([point.x, point.y]);
    setLines([...newLines, lastLine]);
  };

  const handleTouchMove = (e: KonvaEventObject<TouchEvent>) => {
    if (isZooming && e.evt.touches.length === 2) {
      e.evt.preventDefault();
      lastPanTouch.current = null;
      const touch1 = e.evt.touches[0];
      const touch2 = e.evt.touches[1];
      const stage = e.target.getStage();
      const group = transformGroupRef.current;

      if (!stage || !group || !touch1 || !touch2 || !lastCenter.current)
        return;

      const p1 = { x: touch1.clientX, y: touch1.clientY };
      const p2 = { x: touch2.clientX, y: touch2.clientY };

      const newCenter = getCenter(p1, p2);
      const dist = getDistance(p1, p2);

      if (!lastDist.current) {
        lastDist.current = dist;
        return;
      }

      const oldScale = group.scaleX();
      const scaleBy = dist / lastDist.current;
      const newScale = oldScale * scaleBy;

      const minScale = 1;
      const maxScale = 10;
      const clampedScale = Math.max(minScale, Math.min(maxScale, newScale));

      const stageRect = stage.container().getBoundingClientRect();
      const centerPointTo = {
        x: (newCenter.x - stageRect.left - group.x()) / oldScale,
        y: (newCenter.y - stageRect.top - group.y()) / oldScale,
      };

      setScale(clampedScale);
      group.scale({ x: clampedScale, y: clampedScale });

      const newPos =
        clampedScale === 1
          ? { x: 0, y: 0 }
          : {
              x: newCenter.x - stageRect.left - centerPointTo.x * clampedScale,
              y: newCenter.y - stageRect.top - centerPointTo.y * clampedScale,
            };

      group.position(newPos);
      setPosition(newPos);
      stage.batchDraw();

      lastDist.current = dist;
      lastCenter.current = newCenter;
      return;
    }

    if (
      lastPanTouch.current &&
      e.evt.touches.length === 1 &&
      !selectedToolId &&
      scale > 1
    ) {
      e.evt.preventDefault();
      const touch = e.evt.touches[0];
      const stage = e.target.getStage();
      const group = transformGroupRef.current;
      if (!stage || !group) return;

      const deltaX = touch.clientX - lastPanTouch.current.clientX;
      const deltaY = touch.clientY - lastPanTouch.current.clientY;

      const newPos = {
        x: lastPanTouch.current.stageX + deltaX,
        y: lastPanTouch.current.stageY + deltaY,
      };

      lastPanTouch.current = {
        clientX: touch.clientX,
        clientY: touch.clientY,
        stageX: newPos.x,
        stageY: newPos.y,
      };

      group.position(newPos);
      setPosition(newPos);
      stage.batchDraw();
      return;
    }

    handleMouseMove();
  };

  const handleMouseUp = (e: KonvaEventObject<MouseEvent>) => {
    e.evt.preventDefault();
    setIsDrawing(false);
    lastPanMouse.current = null;
  };

  const handleTouchEnd = (e: KonvaEventObject<TouchEvent>) => {
    e.evt.preventDefault();
    setIsDrawing(false);
    setIsZooming(false);
    lastPanTouch.current = null;
    lastCenter.current = null;
    lastDist.current = 0;
  };

  const outerBoxStyles = Object.assign(
    {
      width: outerBoxWidth > 0 ? outerBoxWidth : undefined,
      height: outerBoxHeight > 0 ? outerBoxHeight : undefined,
    },
    boxSx
  );

  return (
    <Box sx={outerBoxStyles} {...boxProps}>
      <div
        ref={containerRef}
        style={{
          width: "100%",
          // "auto" lets iOS Safari steal pinch-zoom before Konva sees it at scale 1.
          touchAction: "none",
          WebkitTouchCallout: "none",
        }}
      >
        {image && fitRect.w > 0 && fitRect.h > 0 && (
          <Stage
            ref={stageRef}
            width={stageWidth}
            height={stageHeight}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{
              zIndex: 5,
              cursor:
                !selectedToolId && scale > 1 ? "grab" : "default",
            }}
          >
            <Layer>
              <Group
                ref={transformGroupRef}
                x={position.x}
                y={position.y}
                scaleX={scale}
                scaleY={scale}
              >
                <KonvaImage
                  image={image}
                  x={fitRect.x}
                  y={fitRect.y}
                  width={fitRect.w}
                  height={fitRect.h}
                />
              </Group>
            </Layer>
            <Layer>
              <Group
                x={position.x}
                y={position.y}
                scaleX={scale}
                scaleY={scale}
              >
                {lines.map((line, i) => (
                  <Line
                    key={i}
                    points={line.points}
                    stroke={line.stroke}
                    opacity={line.opacity}
                    strokeWidth={line.strokeWidth}
                    globalCompositeOperation={line.globalCompositeOperation}
                  />
                ))}
              </Group>
            </Layer>
          </Stage>
        )}
      </div>
      {text && (
        <>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "17em",
              height: "100%",
              background: isDark ? "rgb(20, 20, 20)" : "white",
              padding: "20px 10px",
              borderLeft: "1px solid rgb(120, 105, 238)",
              boxSizing: "border-box",
              overflowY: "auto",
              zIndex: 2,
              transform: isTextOpen ? "translateX(0)" : "translateX(100%)",
              transition: "transform 0.4s ease-in-out",
              display: "flex",
              flexDirection: "row",
              alignItems: "stretch",
              gap: "15px",
            }}
          >
            <button
              onClick={() => setIsTextOpen?.(false)}
              style={{
                width: "25px",
                height: "50px",
                border: "none",
                cursor: "pointer",
                color: "rgb(120, 105, 238)",
                zIndex: 3,
                alignSelf: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IoIosArrowForward />
            </button>
            <Typography
              variant="body2"
              color={isDark ? "common.white" : "text.primary"}
              sx={{
                fontFamily: "Red Hat Display",
                fontWeight: 500,
                cursor: "auto",
                userSelect: "text",
              }}
            >
              <CustomMarkdown>{text}</CustomMarkdown>
            </Typography>
          </Box>
          {!isTextOpen && (
            <button
              onClick={() => setIsTextOpen?.(true)}
              style={{
                position: "absolute",
                top: "calc(50% - 25px)",
                right: "0",
                width: "25px",
                height: "50px",
                borderTopLeftRadius: "25px",
                borderBottomLeftRadius: "25px",
                border: "none",
                cursor: "pointer",
                backgroundColor: "rgb(150, 136, 252)",
                color: "white",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IoIosArrowBack />
            </button>
          )}
        </>
      )}
    </Box>
  );
};
